import { useState, useEffect } from "react"
import { Box, Flex, Text } from "@chakra-ui/react"
import axios from "axios"
import BentoCard from "../BentoCard"
import DetailsModal from "../DetailsModal"
import DormantStock from "../../DormantStock"
import { neumorph } from "../neumorph"
import { formatCurrency, formatNum } from "../KpiHeader"

const DormantStockCard = ({ supplier, retailer }) => {
  const [loading, setLoading] = useState(false)
  const [kpi, setKpi] = useState(null)
  const [items, setItems] = useState([])
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()
    const fetchData = async () => {
      setLoading(true)
      // Never show the previous retailer's figures (2026-10-07); each half fills in on its own.
      setKpi(null)
      setItems([])
      try {
        const userid = localStorage.getItem("userid")
        const [kpiSettled, summarySettled] = await Promise.allSettled([
          axios.post(
            `${process.env.REACT_APP_API_URL}/insights/dormant-stock/kpi`,
            { supplier, retailer, userid, daysThreshold: 90 },
            { signal: controller.signal }
          ),
          axios.post(
            `${process.env.REACT_APP_API_URL}/insights/dormant-stock/summary`,
            { supplier, retailer, userid, daysThreshold: 90 },
            { signal: controller.signal }
          ),
        ])
        if (controller.signal.aborted) return
        const kpiRes = kpiSettled.status === "fulfilled" ? kpiSettled.value : null
        const summaryRes = summarySettled.status === "fulfilled" ? summarySettled.value : null
        if (kpiRes?.data?.status === 1) setKpi(kpiRes.data.data || null)
        if (summaryRes?.data?.status === 1) setItems((summaryRes.data.data || []).slice(0, 5))
      } catch (err) {
        if (!axios.isCancel(err)) console.error("DormantStockCard fetch error:", err)
      } finally {
        // a cancelled request (retailer / supplier switched) must not end the NEW request\'s loading state
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    fetchData()
    return () => controller.abort()
  }, [supplier, retailer])

  const k = kpi || {}

  return (
    <>
      <BentoCard
        title="💤 Dormant Stock"
        subtitle="No sales in 90+ days"
        loading={loading}
        onDetails={() => setModalOpen(true)}
      >
        <Flex direction="column" h="100%" gap={2}>
          <Flex gap={3}>
            <Box>
              <Text fontSize="2xs" color={neumorph.textMuted}>
                Tied-up value
              </Text>
              <Text fontSize="md" fontWeight="700" color={neumorph.accentOrange}>
                {formatCurrency(k.total_dormant_value)}
              </Text>
            </Box>
            <Box>
              <Text fontSize="2xs" color={neumorph.textMuted}>
                Articles
              </Text>
              <Text fontSize="md" fontWeight="700" color={neumorph.textPrimary}>
                {formatNum(k.dormant_article_count)}
              </Text>
            </Box>
            <Box>
              <Text fontSize="2xs" color={neumorph.textMuted}>
                Stores
              </Text>
              <Text fontSize="md" fontWeight="700" color={neumorph.textPrimary}>
                {formatNum(k.dormant_store_count)}
              </Text>
            </Box>
          </Flex>
          {items.length > 0 && (
            <Box mt={2}>
              <Text fontSize="2xs" color={neumorph.textMuted} mb={1} fontWeight="600" textTransform="uppercase">
                Top dormant
              </Text>
              {items.map((r, i) => (
                <Flex
                  key={r.article_code || i}
                  py={1}
                  borderBottom={i < items.length - 1 ? "1px solid rgba(174,184,200,0.15)" : "none"}
                  align="center"
                  gap={2}
                >
                  <Text fontSize="xs" color={neumorph.textPrimary} flex="1" isTruncated>
                    {r.articledescription}
                  </Text>
                  <Text fontSize="xs" fontWeight="600" color={neumorph.accentOrange}>
                    {formatCurrency(r.total_soh_value)}
                  </Text>
                </Flex>
              ))}
            </Box>
          )}
        </Flex>
      </BentoCard>
      <DetailsModal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Dormant Stock Analysis">
        <DormantStock supplier={supplier} retailer={retailer} />
      </DetailsModal>
    </>
  )
}

export default DormantStockCard
